import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import PageHeader from '@hobs/components/PageHeader';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import InputLabel from '@hobs/components/InputLabel';
import Status from '@hobs/components/Status';
import Money from '@hobs/components/Money';
import Loader from '@hobs/components/Loader';
import BluePanel from '@hobs/components/BluePanel';
import PageActions from '@hobs/components/PageActions';
import { H2 } from '@hobs/components/Headings';
import { format } from '@hobs/utils/formatWithTimeZone';
import PageLinks from '@hobs/components/PageLinks';
import CalculateByDays from '@hobs/components/CalculateByDays';

import {
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  DATE_FORMAT,
  TICKETS_STATUSES,
  SERVICE_REQUEST_FOOTER_STATUSES,
  PARTY_TYPE
} from '@hobs/utils/constants';
import './ChangeHistory.scss';
import Quantity from '@hobs/components/Quantity';

const SHORT_NAMES = {
  'Host Nation': 'HN',
  'Sending Nation': 'SN'
};

class ChangeHistory extends PureComponent {
  constructor(props) {
    super(props);

    this.container = React.createRef();
  }

  componentDidMount() {
    const {
      match: {
        params: { serviceRequestId }
      },
      getTickets,
      getFooter
    } = this.props;

    getFooter(serviceRequestId);
    getTickets(serviceRequestId);
  }

  componentWillUnmount() {
    this.props.clearFooter();
    this.props.clearTickets();
  }

  navigateBack = (navContext, previousPageHistory) => () => {
    const { history } = this.props;
    history.push(this.getBackNavigationUrl(navContext, previousPageHistory));
  };

  isActionsEnabled() {
    return (
      this.props.footer &&
      this.props.footer.footerType ===
        SERVICE_REQUEST_FOOTER_STATUSES.RequestForCancellation
    );
  }

  isCustom() {
    return this.props.tickets.isCustom;
  }

  getHistoryPath() {
    const { match } = this.props;
    return `/${
      this.isCustom()
        ? 'custom-service-request-history'
        : 'service-request-history'
    }/${match.params.partyId}/${match.params.serviceRequestId}`;
  }

  getEditPath() {
    const { match } = this.props;
    return `/${
      this.isCustom() ? 'custom-service-request' : 'service-request'
    }/${match.params.partyId}/${match.params.serviceRequestId}`;
  }

  getShowPath() {
    const { match } = this.props;
    return `/request-information/${match.params.serviceRequestId}`;
  }

  getTransportationPath() {
    const { match } = this.props;
    return `/service-request/${match.params.partyId}/${match.params.serviceRequestId}/transportation`;
  }

  getChangeHistoryPath() {
    const { match } = this.props;
    return `/service-request/${match.params.partyId}/${match.params.serviceRequestId}/change-history`;
  }

  getDiscussionPath() {
    const { match } = this.props;
    return `${match.url}/discussion`;
  }

  getDiscussionInitials() {
    const { t } = this.props;
    return t('SN');
  }

  renderNoResults() {
    const { t, footer } = this.props;
    return (
      <div className="change-history--empty">
        <div className="change-history--empty-content">
          {t('There are no change requests for this service.')}

          {this.isActionsEnabled() && (
            <div className="change-history--empty-actions">
              <RaisedButton
                onClick={this.onAddChangeRequest}
                disabled={footer && footer.isChangeDisabled}
                iconLeft={<Icon icon="plus" />}
              >
                {t('Add change request')}
              </RaisedButton>

              <GhostButton
                onClick={this.onRequestForCancellation}
                danger
                disabled={footer && footer.isCancellationDisabled}
                iconLeft={<Icon icon="Close" />}
              >
                {t('Request for cancellation')}
              </GhostButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderHeaderCenterActions = () => {
    return null;
  };

  onAddChangeRequest = () => {
    const {
      history,
      match: {
        params: { serviceRequestId, partyId }
      }
    } = this.props;

    history.push({
      pathname: `/${
        this.isCustom() ? 'custom-service-request' : 'service-request'
      }/${partyId}/${serviceRequestId}`,
      state: {
        showCreateChangeRequestDialog: true
      }
    });
  };

  onRequestForCancellation = () => {
    const {
      history,
      match: {
        params: { serviceRequestId, partyId }
      }
    } = this.props;

    history.push({
      pathname: `/${
        this.isCustom() ? 'custom-service-request' : 'service-request'
      }/${partyId}/${serviceRequestId}`,
      state: {
        showRequestCancellationDialog: true
      }
    });
  };

  getBackNavigationUrl = (navContext, history) => {
    const { match } = this.props;
    if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets) {
      return '/tickets';
    } else if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview) {
      const withSearch =
        history && history.location && history.location.search
          ? history.location.search
          : '';
      return `/annex-overview${withSearch}`;
    } else if (
      navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices
    ) {
      return `/service-requests/${match.params.partyId}`;
    }

    return `/service-requests/${match.params.partyId}`;
  };

  getValues(values) {
    if (typeof values !== 'object') {
      return values;
    }

    return Object.keys(values).reduce((results, key) => {
      results[key] =
        results[key] || typeof results[key] === 'boolean' ? results[key] : '-';
      return results;
    }, values);
  }

  getDisplayValue(value, placeholder = '-') {
    return value && value !== '' ? value : placeholder;
  }

  renderTicketClosed(values, { additionalRequestTypeId }) {
    const { t } = this.props;

    if (!values) {
      return null;
    }

    if (values.accepted) {
      const isAcceptedAutomatically = !values.accepted.fullName;
      const message = this.getRequestConfirmationMessage(
        additionalRequestTypeId,
        isAcceptedAutomatically
      );
      return (
        <>
          <hr />
          <div className="change-history--group">
            <H2>{t('Request confirmation')}</H2>
            <div className="change-history--group-item">
              <div>
                {message}{' '}
                {isAcceptedAutomatically
                  ? ''
                  : values.accepted.partyType === 'Host Nation'
                  ? 'HN '
                  : 'SN '}
                <span className="change-history--gray">
                  (
                  {isAcceptedAutomatically
                    ? ''
                    : values.accepted.fullName + ', '}
                  <time>
                    {format(values.accepted.creationDate, DATE_FORMAT)}
                  </time>
                  )
                </span>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (values.rejected) {
      const message =
        additionalRequestTypeId === 0
          ? t('Additional service request was rejected by the ')
          : t('Change request was rejected by the ');

      return (
        <>
          <hr />
          <div className="change-history--group">
            <H2>{t('Request rejection')}</H2>
            <div className="change-history--group-item">
              <div>
                {message}
                {values.rejected.partyType === 'Host Nation' ? 'HN ' : 'SN '}(
                <span className="change-history--gray">
                  {values.rejected.fullName},{' '}
                  <time>
                    {format(values.rejected.creationDate, DATE_FORMAT)}
                  </time>
                  )
                </span>
              </div>
            </div>
          </div>
        </>
      );
    }
  }

  getRequestConfirmationMessage(
    additionalRequestTypeId,
    isAcceptedAutomatically
  ) {
    const { t } = this.props;

    switch (additionalRequestTypeId) {
      case 0:
        return t(
          `Additional service request was ${
            !isAcceptedAutomatically
              ? 'accepted by the '
              : 'automatically confirmed'
          }`
        );
      case 3:
        return t(
          'Response to the Additional service request was updated by the '
        );
      default:
        return t(
          `Change request was ${
            !isAcceptedAutomatically
              ? 'accepted by the '
              : 'automatically confirmed'
          }`
        );
    }
  }

  renderChangeRequests() {
    const {
      tickets: { tickets },
      tickets: ticketInfo
    } = this.props;

    return tickets.map((ticket, index) => {
      const additionalInfo = {
        partyName: ticketInfo.partyName,
        createdByFullName: ticket.createdByFullName,
        creationDate: ticket.creationDate,
        additionalRequestTypeId: ticket.additionalRequestTypeId,
        creationComment: ticket.creationComment,
        ticketStatus: ticket.ticketStatus,
        createdByPartyType: ticket.createdByPartyType
      };

      return (
        <div key={`ticket_${index}`} className="change-history--wrapper">
          <div className="change-history--info">
            <strong>
              {ticket.additionalRequestType} ({ticket.ticketNumber})
            </strong>{' '}
            <time>{format(ticket.creationDate, DATE_FORMAT)}</time>
            <Status
              name={ticket.ticketStatus}
              type={ticket.ticketStatusId}
              statuses={TICKETS_STATUSES}
              inverted={true}
            />
          </div>
          <div className="change-history--content">
            {this.cancellationRequest(
              ticket.cancellationRequest,
              additionalInfo
            )}
            {this.renderChangeServiceRequestChanges(
              ticket.changeRequest,
              additionalInfo
            )}
            {this.renderNewServiceRequestChanges(
              ticket.newServiceRequest,
              additionalInfo
            )}
            {!ticket.cancellationRequest &&
              this.renderTicketClosed(ticket.ticketClosed, additionalInfo)}
          </div>
        </div>
      );
    });
  }

  cancellationRequest(values, info) {
    if (!values) {
      return null;
    }

    const { responseFromHn } = values;
    const { t } = this.props;

    const responseFromHnData = responseFromHn
      ? this.getValues(responseFromHn)
      : {};
    const message =
      info.ticketStatus === 'Rejected'
        ? t('Request for cancellation was rejected by the HN ')
        : t('Request for cancellation was confirmed by the HN ');

    return (
      <div className="change-history--group">
        <H2>{t('Cancellation request')}</H2>
        <div className="change-history--group-item">
          <div>
            {'Request for cancellation was created for the service by the '}
            {SHORT_NAMES[info.createdByPartyType]}{' '}
            <span className="change-history--response-to-hn-gray">
              ({info.createdByFullName},{' '}
              <time>{format(info.creationDate, DATE_FORMAT)}</time>)
            </span>
          </div>
        </div>

        <div className="change-history--group-item comment">
          <InputLabel>{t('Comment')}</InputLabel>
          <div>{info.creationComment}</div>
        </div>
        {responseFromHn && (
          <>
            <hr />
            <H2>{t('Response from HN')}</H2>
            <div className="change-history--group-item">
              <div>
                {message}
                <span className="change-history--response-to-hn-gray">
                  ({responseFromHnData.fullName},{' '}
                  <time>
                    {format(responseFromHnData.creationDate, DATE_FORMAT)}
                  </time>
                  )
                </span>
              </div>
            </div>
            {this.renderCancellationResponseFromHn(responseFromHnData)}
          </>
        )}
      </div>
    );
  }

  renderServiceRequestChanges(changes, action) {
    const { t, currentExercise } = this.props;

    const getRenderValue = (key, type = 'String') => {
      let renderValue = null;

      const value =
        changes.oldValues &&
        changes.oldValues[key] &&
        changes[key] !== changes.oldValues[key]
          ? changes.oldValues[key]
          : false;

      renderValue = !value && hasOldValue(key) ? '-' : value;

      if (type === 'Date' && value) {
        renderValue = format(value, DATE_FORMAT);
      }

      return renderValue;
    };

    const getOldValue = (key, type = 'String') => {
      const renderValue = getRenderValue(key, type);

      return renderValue ? (
        <span className="change-history--old-value">(was {renderValue})</span>
      ) : null;
    };

    const getOldMoneyValue = key => {
      const renderValue = getRenderValue(key);

      return renderValue ? (
        <span className="change-history--old-value">
          (was {<Money amountRangeString={renderValue} />})
        </span>
      ) : null;
    };

    const getOldAmountValue = key => {
      const renderValue = getRenderValue(key);

      return renderValue ? (
        <span className="change-history--old-value">
          (was {<Quantity amount={renderValue} />})
        </span>
      ) : null;
    };

    const hasOldValue = key => {
      return changes.oldValues && changes[key] !== changes.oldValues[key];
    };

    const isResponsiblePersonSet = changes.createdByFullName !== '';
    const message =
      action === 'NEW'
        ? t(
            `New additional service request was created ${
              isResponsiblePersonSet ? 'by the ' : 'automatically'
            } `
          )
        : t('Change request was created for the service by the ');

    return (
      <div className="change-history--group change-history--sr-information">
        <H2>
          {action === 'NEW'
            ? t('Service request information')
            : t('Change request information')}
        </H2>

        <div className="change-history--group-row">
          <div className="change-history--group-item">
            {message}
            {isResponsiblePersonSet &&
              SHORT_NAMES[changes.createdByPartyType]}{' '}
            <span className="change-history--sr-information-gray">
              ({isResponsiblePersonSet && changes.createdByFullName + ' '}
              <time>{format(changes.creationDate, DATE_FORMAT)}</time>)
            </span>
          </div>
        </div>

        <div className="change-history--group-row">
          <div>
            <div className="change-history--group-row">
              <div className="change-history--group-item">
                <InputLabel indicationMessage={hasOldValue('toUnit')}>
                  {t('To Unit')}
                </InputLabel>
                <div>
                  {changes.toUnit} {getOldValue('toUnit')}
                </div>
              </div>
              <div className="change-history--group-item">
                <InputLabel
                  indicationMessage={hasOldValue('requestedQuantity')}
                >
                  {t('Req. q-ty')}
                </InputLabel>
                <div>
                  <Quantity amount={changes.requestedQuantity} />{' '}
                  {getOldAmountValue('requestedQuantity')}
                </div>
              </div>
              <div className="change-history--group-item">
                <InputLabel indicationMessage={hasOldValue('unitOfMeasure')}>
                  {t('UoM')}
                </InputLabel>
                <div>
                  {changes.unitOfMeasure} {getOldValue('unitOfMeasure')}
                </div>
              </div>
            </div>

            <div className="change-history--group-row">
              <div className="change-history--group-item">
                <InputLabel indicationMessage={hasOldValue('fromDate')}>
                  {t('From date')}
                </InputLabel>
                <div>
                  {format(changes.fromDate, DATE_FORMAT)}{' '}
                  {getOldValue('fromDate', 'Date')}
                </div>
              </div>
              <div className="change-history--group-item">
                <InputLabel indicationMessage={hasOldValue('toDate')}>
                  {t('To date')}
                </InputLabel>
                <div>
                  {format(changes.toDate, DATE_FORMAT)}{' '}
                  {getOldValue('toDate', 'Date')}
                </div>
              </div>
              <div className="change-history--group-item">
                <CalculateByDays
                  indicationMessage={hasOldValue('calculateByDays')}
                  disabled={true}
                  checked={changes.calculateByDays}
                  differenceInDays={
                    changes.totalDays ? changes.totalDays : null
                  }
                >
                  &nbsp;
                  {getOldValue('totalDays')}
                </CalculateByDays>
              </div>
            </div>

            <div className="change-history--group-row">
              <div className="change-history--group-item">
                <InputLabel indicationMessage={hasOldValue('locationFromName')}>
                  {t(this.isTransportation() ? 'Location from' : 'Location')}
                </InputLabel>
                <div>
                  {this.getDisplayValue(changes.locationFromName)}{' '}
                  {getOldValue('locationFromName')}
                </div>
              </div>
              <div className="change-history--group-item">
                <InputLabel
                  indicationMessage={hasOldValue('locationFromComment')}
                >
                  {t(
                    this.isTransportation()
                      ? 'Location from comment'
                      : 'Location comment'
                  )}
                </InputLabel>
                <div>
                  {this.getDisplayValue(changes.locationFromComment)}{' '}
                  {getOldValue('locationFromComment')}
                </div>
              </div>
            </div>

            {this.isTransportation() && (
              <div className="change-history--group-row">
                <div className="change-history--group-item">
                  <InputLabel indicationMessage={hasOldValue('locationToName')}>
                    {t('Location to')}
                  </InputLabel>
                  <div>
                    {this.getDisplayValue(changes.locationToName)}{' '}
                    {getOldValue('locationToName')}
                  </div>
                </div>
                <div className="change-history--group-item">
                  <InputLabel
                    indicationMessage={hasOldValue('locationToComment')}
                  >
                    {t('Location to comment')}
                  </InputLabel>
                  <div>
                    {this.getDisplayValue(changes.locationToComment)}{' '}
                    {getOldValue('locationToComment')}
                  </div>
                </div>
              </div>
            )}
            <div className="change-history--group-row">
              <div className="change-history--group-item comment">
                <InputLabel>
                  {t('Comment ')}{' '}
                  {action !== 'NEW' ? t('(why change is needed)') : ''}
                </InputLabel>
                <div>{this.getDisplayValue(changes.creationComment)} </div>
              </div>
            </div>
          </div>

          <div className="change-history--cost-panel">
            <BluePanel>
              <div className="item">
                <div>{t('Total quantity')}</div>
                <InputLabel indicationMessage={hasOldValue('totalQuantity')}>
                  <strong>
                    <Quantity amount={changes.totalQuantity} />{' '}
                    {getOldAmountValue('totalQuantity')}
                  </strong>
                </InputLabel>
              </div>
              <div className="item">
                <div>
                  {t('Est. total cost')}
                  {` (${currentExercise.currency.name})`}
                </div>
                <InputLabel
                  indicationMessage={hasOldValue('estimatedTotalCost')}
                >
                  <strong>
                    <Money
                      amountRangeString={changes.estimatedTotalCost || '-'}
                    />{' '}
                    {getOldMoneyValue('estimatedTotalCost')}
                  </strong>
                </InputLabel>
              </div>
            </BluePanel>
          </div>
        </div>
      </div>
    );
  }

  isTransportation() {
    return this.props.tickets.isTransportation;
  }

  renderNewServiceRequestChanges(values, info) {
    if (!values) {
      return null;
    }

    const { newValues, responseFromHn } = values;

    const {
      createdByFullName,
      creationComment,
      creationDate,
      partyName,
      createdByPartyType
    } = info;

    const newValuesData = this.getValues(newValues);

    return (
      <>
        {this.renderServiceRequestChanges(
          {
            ...newValuesData,
            createdByFullName,
            creationDate,
            partyName,
            creationComment,
            createdByPartyType
          },
          'NEW'
        )}
        {this.renderResponseFromHn(responseFromHn, 'NEW')}
      </>
    );
  }

  renderCancellationResponseFromHn(values) {
    const { t, currentExercise } = this.props;

    if (!values.total || !values.grossAmount) {
      return (
        <div className="change-history--group-item comment">
          <InputLabel>{t('Comment')}</InputLabel>
          <div>{values.comment}</div>
        </div>
      );
    }

    return (
      <>
        <hr />
        <div className="change-history--group change-history--response-to-hn">
          <H2>{t('Cancellation Costs')}</H2>
          <div className="change-history--group-row">
            <div className="change-history--group-item">
              <InputLabel>{t('Q-ty')}</InputLabel>
              <div>
                {!isNaN(values.quantity) ? (
                  <Quantity amount={values.quantity} />
                ) : (
                  0
                )}
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Currency')}</InputLabel>
              <div>
                <span>{currentExercise.currency.name}</span>
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Price')}</InputLabel>
              <div>
                <Money amount={values.price | 0} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Total')}</InputLabel>
              <div>
                <Money amount={values.total} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Gross amount')}</InputLabel>
              <div>
                <Money amount={values.grossAmount} />
              </div>
            </div>
            <div className="change-history--group-item comment">
              <InputLabel>{t('Comment')}</InputLabel>
              <div>{values.comment}</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  renderResponseFromHn({ confirmed, rejected }, action) {
    const { t, currentExercise } = this.props;

    if (!confirmed && !rejected) {
      return null;
    }

    const values = confirmed
      ? this.getValues(confirmed)
      : this.getValues(rejected);

    if (rejected) {
      const message =
        action === 'NEW'
          ? t('Additional service request was rejected by the HN ')
          : t('Change request was rejected by the HN ');
      return (
        <>
          <hr />
          <div className="change-history--group change-history--response-to-hn change-history--response-to-hn-rejected">
            <H2>{t('Response from HN')}</H2>
            <div>
              {message}
              <span className=" change-history--response-to-hn-gray">
                ({values.fullName},
                <time>{format(values.creationDate, DATE_FORMAT)}</time>)
              </span>
            </div>
            <div>
              <InputLabel>{t('Comment')}</InputLabel>
              <div>{values.comment}</div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <hr />
        <div className="change-history--group change-history--response-to-hn">
          <H2>{t('Response from HN')}</H2>
          <div className="change-history--group-row">
            <div className="change-history--group-item">
              <InputLabel>{t('Q-ty')}</InputLabel>
              <div>
                <Quantity amount={values.quantity} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Currency')}</InputLabel>
              <div>
                <span>{currentExercise.currency.name}</span>
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Price')}</InputLabel>
              <div>
                <Money amount={values.price} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Total')}</InputLabel>
              <div>
                <Money amount={values.total} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Gross amount')}</InputLabel>
              <div>
                <Money amount={values.grossAmount} />
              </div>
            </div>
            <div className="change-history--group-item comment">
              <InputLabel>{t('Comment')}</InputLabel>
              <div>{values.comment}</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  renderLastConfirmedValues(values) {
    const { t, currentExercise } = this.props;

    if (!values) {
      return null;
    }

    const data = this.getValues(values);

    return (
      <>
        <hr />
        <div className="change-history--group change-history--response-to-hn">
          <H2>{t('Last confirmed values')}</H2>
          <div className="change-history--group-row">
            <div className="change-history--group-item">
              <InputLabel>{t('Q-ty')}</InputLabel>
              <div>
                <Quantity amount={data.quantity} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Currency')}</InputLabel>
              <div>
                <span>{currentExercise.currency.name}</span>
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Price')}</InputLabel>
              <div>
                <Money amount={data.price} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Total')}</InputLabel>
              <div>
                <Money amount={data.total} />
              </div>
            </div>
            <div className="change-history--group-item">
              <InputLabel>{t('Gross amount')}</InputLabel>
              <div>
                <Money amount={data.grossAmount} />
              </div>
            </div>
            <div className="change-history--group-item comment">
              <InputLabel>{t('Comment')}</InputLabel>
              <div>{data.comment}</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  renderChangeServiceRequestChanges(values, info) {
    if (!values) {
      return null;
    }

    const {
      newValues,
      responseFromHn,
      lastConfirmedValues,
      oldValues
    } = values;

    const {
      createdByFullName,
      creationDate,
      creationComment,
      partyName,
      createdByPartyType
    } = info;

    return (
      <>
        {this.renderServiceRequestChanges(
          {
            ...newValues,
            createdByFullName,
            creationDate,
            partyName,
            oldValues,
            creationComment,
            createdByPartyType
          },
          'CHANGE'
        )}
        {this.renderLastConfirmedValues(lastConfirmedValues)}
        {this.renderResponseFromHn(responseFromHn, 'CHANGE')}
      </>
    );
  }

  render() {
    const {
      t,
      tickets: { tickets },
      tickets: info,
      isLoading,
      match: { params },
      footer,
      getTickets
    } = this.props;
    return (
      <div ref={this.container} className="change-history">
        {isLoading && <Loader />}
        {!isLoading && (
          <div className="change-history--heading">
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <>
                  {this.props.withEscNavigationCallback(
                    this.navigateBack(
                      nav.serviceRequestNavigation,
                      nav.previousPageHistory
                    )
                  )}
                  <PageHeader
                    to={this.getBackNavigationUrl(
                      nav.serviceRequestNavigation,
                      nav.previousPageHistory
                    )}
                    title={t('Change history')}
                    unit={{
                      code: info.partyCode,
                      name: info.unitName,
                      number: info.requestNumber
                    }}
                    status={{
                      name: info.statusName,
                      id: info.statusId
                    }}
                  />
                </>
              )}
            </ServiceRequestNavContext.Consumer>
            {this.renderHeaderCenterActions()}
            <PageLinks
              isTransportation={this.isTransportation()}
              serviceRequestId={params.serviceRequestId}
              partyId={params.partyId}
              discussion={{
                status: info.discussionStatusId,
                initials: this.getDiscussionInitials(),
                onDiscussionClose: () => getTickets(params.serviceRequestId),
                isSeen: info.isLastDiscussionSeen
              }}
              transportationPath={this.getTransportationPath()}
              changeHistoryPath={this.getChangeHistoryPath()}
              showPath={this.getShowPath()}
              editPath={this.getEditPath()}
              historyPath={this.getHistoryPath()}
              discussionPath={this.getDiscussionPath()}
            />
          </div>
        )}

        {!isLoading && tickets.length === 0 && this.renderNoResults()}
        {!isLoading && tickets.length > 0 && this.renderChangeRequests()}

        {!isLoading && this.isActionsEnabled() && (
          <PageActions container={this.container.current}>
            <div />
            <div className="change-history--footer">
              <RaisedButton
                type="button"
                large
                iconLeft={<Icon icon="plus" />}
                disabled={footer && footer.isChangeDisabled}
                onClick={this.onAddChangeRequest}
              >
                {t('Add change request')}
              </RaisedButton>
              <GhostButton
                type="button"
                danger
                large
                disabled={footer && footer.isCancellationDisabled}
                onClick={this.onRequestForCancellation}
                iconLeft={<Icon icon="Close" />}
              >
                {t('Request for cancellation')}
              </GhostButton>
            </div>
          </PageActions>
        )}
      </div>
    );
  }
}

ChangeHistory.propTypes = {
  isLoading: PropTypes.bool,
  t: PropTypes.func,
  tickets: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  getTickets: PropTypes.func,
  clearTickets: PropTypes.func,
  getFooter: PropTypes.func,
  clearFooter: PropTypes.func,
  footer: PropTypes.object,
  withEscNavigationCallback: PropTypes.func,
  currentExercise: PropTypes.object
};

export default ChangeHistory;
